import React, { useEffect, useRef, useState } from 'react';
import { NetSuiteBillPayload } from '../types';
import { coercePayload } from '../utils/netsuite';

interface JsonPayloadPaneProps {
  payload: NetSuiteBillPayload;
  onChange: (next: NetSuiteBillPayload) => void;
  /** Reports whether the editor currently holds JSON that can't be sent. */
  onInvalidChange: (invalid: boolean) => void;
  disabled: boolean;
}

const pretty = (payload: NetSuiteBillPayload) => JSON.stringify(payload, null, 2);

const TOKEN_RE = /("(?:\\.|[^"\\])*")(\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;

/** Minimal JSON syntax highlighting as React nodes (no innerHTML). */
function highlight(json: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(json)) !== null) {
    const [token, str, colon] = match;
    if (match.index > last) nodes.push(json.slice(last, match.index));
    if (str && colon) {
      nodes.push(
        <span key={match.index} className="nsb__tok nsb__tok--key">
          {str}
        </span>,
        colon
      );
    } else {
      const kind = str
        ? 'string'
        : token === 'null'
          ? 'null'
          : token === 'true' || token === 'false'
            ? 'bool'
            : 'number';
      nodes.push(
        <span
          key={match.index}
          className={`nsb__tok nsb__tok--${kind}`}
          title={kind === 'null' ? 'Missing value' : undefined}
        >
          {token}
        </span>
      );
    }
    last = match.index + token.length;
  }
  if (last < json.length) nodes.push(json.slice(last));
  return nodes;
}

const JsonPayloadPane: React.FC<JsonPayloadPaneProps> = ({
  payload,
  onChange,
  onInvalidChange,
  disabled,
}) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(() => pretty(payload));
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // The payload this editor last emitted. When the incoming payload matches it
  // the change came from typing here, so the text (and caret) is left alone;
  // anything else came from the form and replaces the text.
  const emitted = useRef<string | null>(null);
  useEffect(() => {
    if (JSON.stringify(payload) === emitted.current) return;
    setText(pretty(payload));
    setError(null);
  }, [payload]);

  useEffect(() => {
    onInvalidChange(error !== null);
  }, [error, onInvalidChange]);

  const handleText = (value: string) => {
    setText(value);
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON.');
      return;
    }
    const result = coercePayload(parsed);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    setError(null);
    emitted.current = JSON.stringify(result.payload);
    onChange(result.payload);
  };

  const toggleEditing = () => {
    if (editing) {
      // Done: normalise the formatting and drop an unfinished (invalid) edit.
      setText(pretty(payload));
      setError(null);
    }
    setEditing(!editing);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pretty(payload));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (non-secure context) */
    }
  };

  const status = error
    ? error
    : disabled && !editing
      ? 'Exactly what will be sent next.'
      : editing
        ? 'Valid JSON — changes sync to the form as you type. Extra properties are sent as-is; subsidiaryId and requestorId stay fixed.'
        : "Exactly what will be sent next. Edit it to change values or add properties the form doesn't cover.";

  return (
    <section className="nsb__json" aria-labelledby="nsb-json-title">
      <header className="nsb__json-head">
        <div className="nsb__json-heading">
          <h3 className="nsb__json-title" id="nsb-json-title">
            Request payload
          </h3>
          <span className="nsb__json-method">POST · JSON</span>
        </div>
        <div className="nsb__json-actions">
          <button type="button" className="nsb__chip-btn" onClick={copy}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
          <button
            type="button"
            className={`nsb__chip-btn${editing ? ' is-active' : ''}`}
            onClick={toggleEditing}
            disabled={disabled}
            aria-pressed={editing}
          >
            {editing ? 'Done' : '✎ Edit JSON'}
          </button>
        </div>
      </header>

      {editing ? (
        <textarea
          className={`nsb__json-editor${error ? ' is-invalid' : ''}`}
          value={text}
          onChange={(e) => handleText(e.target.value)}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          disabled={disabled}
          aria-label="Payload JSON"
          aria-invalid={error ? true : undefined}
          aria-describedby="nsb-json-status"
        />
      ) : (
        <pre className="nsb__json-code" tabIndex={0} aria-label="Payload JSON preview">
          <code>{highlight(text)}</code>
        </pre>
      )}

      <p
        id="nsb-json-status"
        className={`nsb__json-status${error ? ' is-error' : ''}`}
        role={error ? 'alert' : undefined}
      >
        {error && <span aria-hidden="true">⚠ </span>}
        {status}
      </p>
    </section>
  );
};

export default JsonPayloadPane;
